import { QRCodeSVG } from 'qrcode.react';
import React, { FC, Fragment, useEffect, useRef } from 'react';
import { Work } from './api';
import { useLoading, useSearchCondition, useSearchParameters, useTotalWorks, useWorks } from './context';
import { isMobile } from './utils';
import styles from './work.module.scss';

type WorkTaxonomiesProps = {
  work: Work;
  wrapClass: string;
  taxonomyClass: string;
  termClass: string;
};
export const WorkTaxonomies: FC<WorkTaxonomiesProps> = (props) => {
  const { work, wrapClass, taxonomyClass, termClass } = props;

  return (
    <div className={wrapClass}>
      <span className={taxonomyClass}>業種</span>
      <span className={termClass}>
        {(work.terms.works_industry ?? []).map((term, i) => (
          <Fragment key={term.slug}>
            {i > 0 && ', '}
            <span key={term.slug}>{term.name}</span>
          </Fragment>
        ))}
      </span>
      <span className={taxonomyClass}>カスタマイズ</span>
      <span className={termClass}>
        {(work.terms.works_formatline_option ?? []).map((term, i) => (
          <Fragment key={term.slug}>
            {i > 0 && ', '}
            <span key={term.slug}>{term.name}</span>
          </Fragment>
        ))}
      </span>
      <span className={taxonomyClass}>デザイン</span>
      <span className={termClass}>
        {(work.terms.works_formatline_taste ?? []).map((term, i) => (
          <Fragment key={term.slug}>
            {i > 0 && ', '}
            <span key={term.slug}>{term.name}</span>
          </Fragment>
        ))}
      </span>
    </div>
  );
};

export const WorkItem: FC<{ work: Work }> = (props) => {
  const { work } = props;

  const mobileUrl = work.meta.otherLinkMobile ? work.meta.otherLinkMobile : work.meta.otherLink;
  const url = isMobile() ? mobileUrl : work.meta.otherLink;
  return (
    <article className={styles.work}>
      <a href={url} target='_blank' className={styles.workImage} rel='noreferrer'>
        <img src={work.meta.imageUrl ?? 'https://ralsnet.jp/resources/common/img/noimage.png'} alt='' />
        {!isMobile() && <QRCodeSVG value={mobileUrl} includeMargin className={styles.workQR} />}
      </a>
      <WorkTaxonomies
        work={work}
        wrapClass={styles.workTaxonomies}
        taxonomyClass={styles.workTaxonomy}
        termClass={styles.workTerm}
      />
    </article>
  );
};

export const WorkItemSkelton: FC = () => (
  <article className={`${styles.work} ${styles.skelton}`}>
    <div className={styles.workImage}> </div>
    <div className={styles.workTaxonomies}>
      <span className={styles.workTaxonomy}> </span>
      <span className={styles.workTaxonomy}> </span>
      <span className={styles.workTaxonomy}> </span>
    </div>
  </article>
);

export const WorkList: FC = () => {
  const works = useWorks();
  const total = useTotalWorks();
  const loading = useLoading();
  const search = useSearchParameters();
  const condition = useSearchCondition();

  const listRef = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (!first.current && listRef.current) {
      window.scrollTo({
        top: listRef.current.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [search.page]);

  useEffect(() => {
    first.current = false;
    return () => {
      first.current = true;
    };
  }, []);

  if (loading) {
    const arr = new Array(search.limit).fill(0).map((_, i) => i + 1);
    return (
      <div ref={listRef} className={styles.list}>
        {arr.map((p) => (
          <WorkItemSkelton key={p} />
        ))}
      </div>
    );
  }

  const from = (search.page - 1) * search.limit + 1;
  const to = (search.page - 1) * search.limit + works.length;

  return (
    <>
      <div className={styles.condition}>
        <p className={styles.conditionSeek} aria-hidden={total === 0}>
          {total}件中 {from}～{to}件目を表示中
        </p>
        {condition && <p className={styles.conditionText}>{condition}で絞り込み</p>}
      </div>
      {total === 0 && (
        <div className={styles.notfound}>
          <p className={styles.notfoundMessage}>条件にあてはまる制作事例が見つかりませんでした。</p>
          <p className={styles.notfoundAdvice}>検索条件をご確認の上、再度お試しください。</p>
        </div>
      )}
      <div className={styles.list}>
        {works.map((work) => (
          <WorkItem key={work.id} work={work} />
        ))}
      </div>
    </>
  );
};
